import environment from '../environment'
import { getAccessToken } from '../shared/utils/auth/auth'
import joinUrl from '../shared/utils/joinUrl'
import type { DetailResponse, QueryParams, ServiceDefinition } from '../types/mapDetailTypes'
import { handleErrors } from '../shared/utils/api/api'

const createRequestHeaders = (headers?: Headers) => {
  if (!headers) {
    return new Headers({
      'X-Api-Key': environment.DSO_API_KEY,
    })
  }

  headers.append('X-Api-Key', environment.DSO_API_KEY)
  return headers
}

const createSearchParams = (
  id: string | null | undefined,
  extraParams: QueryParams | undefined,
) => {
  const { dynamicIdParam, ...params } = extraParams ?? {}
  let searchParams = params ? new URLSearchParams(params).toString() : null

  if (dynamicIdParam && id && params) {
    searchParams = `${dynamicIdParam}=${id}&${searchParams as string}`
  }

  if (dynamicIdParam && id) {
    searchParams = `${dynamicIdParam}=${id}`
  }

  if (params && extraParams?.dynamicIdParam) {
    const extraParamsStr = Object.keys(params)
      .map((key: string) => `${key}=${params[key]}`)
      .join('&')

    if (searchParams) {
      searchParams = `${searchParams}&${extraParamsStr}`
    } else {
      searchParams = `${extraParamsStr}`
    }
  }

  return searchParams
}

export const fetchData = <Type = any>(
  url: string,
  headers?: Headers,
  options?: any,
): Promise<Type> => {
  const requestHeaders = createRequestHeaders(headers)
  const token = getAccessToken()

  if (token?.length > 0) {
    requestHeaders.set('Authorization', `Bearer ${token}`)
  }

  const fetchOptions = options
    ? { ...options, headers: requestHeaders }
    : { headers: requestHeaders }

  return fetch(url, fetchOptions)
    .then((response) => handleErrors(response))
    .then((response) => response.json() as Promise<Type>)
}

export const getFetchUrl = (endpoint: string, id?: string | null, extraParams?: QueryParams) => {
  const apiRoot = environment.DSO_API_ROOT
  const { dynamicIdParam } = extraParams ?? {}
  const searchParams = createSearchParams(id, extraParams)

  const staticId = dynamicIdParam ? null : id
  const url = new URL(joinUrl(staticId ? [apiRoot, endpoint, staticId] : [apiRoot, endpoint], true))

  return searchParams ? `${url.toString()}?${searchParams}` : url.toString()
}

export const getChildRequestsData = async (
  serviceDefinition: ServiceDefinition,
  responseData: any,
): Promise<DetailResponse> => {
  const data = (await serviceDefinition.childRequests?.(responseData.data)) ?? responseData.data

  return {
    ...responseData,
    data,
  }
}

const getData = async (
  serviceDefinition: ServiceDefinition,
  id: string,
): Promise<DetailResponse> => {
  const fetchUrl = getFetchUrl(
    serviceDefinition.endpoint,
    id,
    serviceDefinition.endpointParams ?? {},
  )

  // TODO which APIs does should this be valid for? Presumably anything starting with v1 that is NOT DSO?
  // TODO: Remove this when we no longer have v1 endpoints.
  // const supportsCrs = serviceDefinition.endpoint?.startsWith('v1')
  const supportsCrs = false

  // Add request headers to retrieve coordinates in WGS84.
  const headers = new Headers(supportsCrs ? { 'Accept-Crs': 'EPSG:4258' } : undefined)

  const responseData = await fetchData(fetchUrl, headers)
  const heading = serviceDefinition.getHeading && serviceDefinition.getHeading(responseData)
  const geometryLocation = serviceDefinition.getMapGeometry(responseData)

  return {
    data: responseData,
    heading,
    ...geometryLocation,
  }
}

export default getData
